import { msg } from "@lingui/core/macro"
import { useLingui } from "@lingui/react"
import { Trans } from "@lingui/react/macro"
import { Alert, Anchor, Button, Checkbox, Divider, Image, Paper, PasswordInput, Stack, TextInput } from "@mantine/core"
import { useCallback, useState } from "react"
import { Logo } from "../../../components/Logo"
import { LoginHero } from "../LoginHero"
import { LoginTagline } from "../LoginTagline"
import type { IViewProps } from "./interfaces"

/**
 * Login screen — pure presentation. The smart route wrapper owns auth state,
 * Better Auth sign-in / SSO, the 2FA redirect and error/loading state; this
 * view renders the full-bleed brand-art background with a frosted-glass card,
 * the branding logo, the error alert and the credential form, emitting
 * `onSubmit` / `onSSO` outward.
 *
 * Form field state lives here (presentation-only) so the wrapper stays free of
 * input plumbing — the entered values surface to the wrapper via `onSubmit`.
 */
export const LoginView = ({
  product,
  branding,
  displayName,
  error,
  loading,
  useArtBackground,
  showRememberMe,
  onSubmit,
  onSSO,
}: IViewProps): React.JSX.Element => {
  const [email, setEmail] = useState("")
  const [password, setPassword] = useState("")
  const [rememberMe, setRememberMe] = useState(false)
  const { i18n } = useLingui()

  const handleSubmit = useCallback(
    (e: React.FormEvent): void => {
      e.preventDefault()
      onSubmit(email, password, rememberMe)
    },
    [email, password, rememberMe, onSubmit],
  )

  return (
    <div
      className={`login-page${useArtBackground ? " login-page--art" : ""}`}
      style={branding?.loginBackground ? { backgroundImage: `url('${branding.loginBackground}')` } : undefined}
    >
      <div className="login-content">
        {useArtBackground && <LoginHero />}
        <Paper
          radius="md"
          p="xl"
          withBorder
          bg={useArtBackground || product === "voxcanon" ? undefined : "var(--mantine-color-body)"}
          style={useArtBackground ? undefined : { opacity: 0.95 }}
        >
          {/* The art-background layout carries the brand in <LoginHero> above the
              card, so the card itself drops the logo to avoid doubling up. */}
          {!useArtBackground && (
            <Stack align="center" mb="md">
              {branding?.logo ? (
                <Image src={branding.logo} alt={displayName} className="login-logo" fit="contain" />
              ) : (
                <Logo size={40} />
              )}
            </Stack>
          )}

          {error && (
            <Alert color="danger" mb="md" variant="light">
              {error}
            </Alert>
          )}

          <form onSubmit={handleSubmit}>
            <Stack>
              <TextInput
                label={i18n._(msg`Email`)}
                placeholder="you@company.com"
                value={email}
                onChange={(e) => setEmail(e.currentTarget.value)}
                required
                autoFocus
              />
              <PasswordInput
                label={i18n._(msg`Password`)}
                placeholder={i18n._(msg`Your password`)}
                value={password}
                onChange={(e) => setPassword(e.currentTarget.value)}
                required
              />
              {showRememberMe && (
                <Checkbox
                  label={i18n._(msg`Keep me signed in`)}
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.currentTarget.checked)}
                />
              )}
              <Button type="submit" fullWidth loading={loading}>
                <Trans>Sign in</Trans>
              </Button>
              <Anchor href="/forgot-password" size="sm" ta="center">
                <Trans>Forgot password?</Trans>
              </Anchor>
            </Stack>
          </form>

          {branding?.ssoEnabled && (
            <>
              <Divider label={i18n._(msg`or`)} labelPosition="center" my="md" />
              <Button variant="outline" fullWidth onClick={onSSO} loading={loading}>
                <Trans>Sign in with SSO</Trans>
              </Button>
            </>
          )}
        </Paper>
        {useArtBackground && <LoginTagline />}
      </div>
    </div>
  )
}
